import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth, useToast } from "../../hooks";
import { EmptyContent, Loader, Toast } from "../../components";

import { useSubscriptionPlans, useSubscriptionStatusHandler, useHandleSubscription } from "./hooks";
import { PricingCard } from "./components";

import styles from "./PlansPage.module.scss";

const PLAN_PERIODS = {
	MONTHLY: "monthly",
	YEARLY: "yearly",
};

function PlansPage() {
	const [subscriptionPeriod, setSubscriptionPeriod] = useState(PLAN_PERIODS.MONTHLY);
	const { toastInfo, showToast, clearToast } = useToast();
	const { message: subscriptionStatusMessage, clearMessage: clearSubscriptionStatusMessage } = useSubscriptionStatusHandler(3000);
	const { user, setUser, isLoading: isUserLoading } = useAuth();
	const { monthlyData, yearlyData, error: plansError, loading: plansLoading } = useSubscriptionPlans();
	const activePlanId = user?.activePlanId;

	const plans = useMemo(() => {
		return subscriptionPeriod === PLAN_PERIODS.MONTHLY ? monthlyData || [] : yearlyData || [];
	}, [subscriptionPeriod, monthlyData, yearlyData]);

	const handleSubscription = useHandleSubscription(activePlanId);
	const navigate = useNavigate();

	const isLoading = plansLoading || isUserLoading;
	if (plansError) return <div className={styles.centeredMessage}>Error loading subscription plans. Please refresh.</div>;
	if (isLoading) return <Loader loading={isLoading} />

	const onSubscribe = async (planId) => {
		const result = await handleSubscription(planId);
		if (result.success) {
			setUser({ ...user, activePlanId: planId });
			window.location.replace(result.url);
		} else {
			showToast(result.message);
			if (result.message === "You already have an active subscription.") {
				navigate("/app/settings", { replace: true });
			}
		}
	};

	return (
		<>
			<div className={styles.plansPageContainer}>
				<h1 className={styles.pageTitle}>Our Subscription Plans</h1>

				<div className={styles.toggleContainer} role="radiogroup" aria-label="Subscription Period">
					<button
						className={`${styles.toggleButton} ${subscriptionPeriod === PLAN_PERIODS.MONTHLY ? styles.activeToggle : ""}`}
						onClick={() => setSubscriptionPeriod(PLAN_PERIODS.MONTHLY)}
					>
						Monthly
					</button>
					<button
						className={`${styles.toggleButton} ${subscriptionPeriod === PLAN_PERIODS.YEARLY ? styles.activeToggle : ""}`}
						onClick={() => setSubscriptionPeriod(PLAN_PERIODS.YEARLY)}
					>
						Yearly
					</button>
				</div>

				<div className={styles.plansContainer}>
					{Array.isArray(plans) && plans.length > 0 ? (
						plans.map((plan, index) => (
							<PricingCard
								key={plan.id}
								plan={plan}
								isSuggested={plan.name?.toLowerCase().includes('standard') || index === 1}
								isActivePlan={plan.id === activePlanId}
								onSubscribe={() => onSubscribe(plan.id)}
							/>
						))
					) : (
						<EmptyContent message="No subscription plans available" />
					)}
				</div>
			</div>

			{toastInfo.message && (
				<Toast message={toastInfo.message} type={toastInfo.type} onClose={clearToast} />
			)}
			{subscriptionStatusMessage && (
				<Toast message={subscriptionStatusMessage} type="info" onClose={clearSubscriptionStatusMessage} />
			)}
		</>
	);
}

export { PlansPage };
